import { type RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react';
import { handleError } from '@pn/core/errors/handleError';
import { isCordova } from '@pn/core/utils/env';

declare global {
  interface Window {
    SafariViewController: any;
  }
}

type LoginParams = {
  returnTo?: string;
  type?: 'login' | 'signup';
  connection?: string;
  prefilledEmail?: string;
};

export function useLogin(): (params?: LoginParams) => void {
  const { loginWithRedirect } = useAuth0();

  return async (params) => {
    const args: RedirectLoginOptions = {};
    const authorizationParams: Record<string, string> = {};

    if (params?.returnTo) {
      args.appState = { returnTo: params.returnTo };
    }
    if (params?.type === 'signup') {
      authorizationParams.screen_hint = 'signup';
    }
    if (params?.connection) {
      authorizationParams.connection = params.connection;
    }
    if (params?.prefilledEmail) {
      authorizationParams.login_hint = params.prefilledEmail;
    }

    if (Object.keys(authorizationParams).length > 0) {
      args.authorizationParams = authorizationParams;
    }

    if (isCordova()) {
      await loginWithRedirect({
        ...args,
        authorizationParams: {
          ...args.authorizationParams,
          prompt: 'login', // fixes issue where Auth0 re-uses last logged in user
          redirect_uri: 'petroninja://callback',
        },
        openUrl: (url) =>
          new Promise<void>((resolve, reject) => {
            window.SafariViewController.isAvailable(function (
              available: boolean
            ) {
              if (available) {
                window.SafariViewController.show(
                  {
                    url,
                    hidden: false, // default false
                    animated: true, // default true, note that 'hide' will reuse this preference (the 'Done' button will always animate though)
                  },
                  function (_result: { event: string }) {},
                  function (msg: unknown) {
                    handleError({ error: new Error(JSON.stringify(msg)) });
                    window.alert(
                      'Your device does not support this authentication protocol. Please reach out at hello@stackdx.com for assistance.'
                    );
                    reject(new Error(JSON.stringify(msg)));
                  }
                );
                resolve();
              } else {
                window.cordova.InAppBrowser.open(url, '_system');
                resolve();
              }
            });
          }),
      });
    } else {
      loginWithRedirect(args);
    }
  };
}
